import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { FC } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { DateTime } from "luxon"
import fp from "lodash/fp"
import {
  Alert,
  Box,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/system"
import CloseIcon from "@mui/icons-material/Close"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"

import type { EntityType } from "types/common.types"
import type { CsvDateRange, CsvDownloadFormat, CsvDownloadGroup } from "types/csv.types"
import useTelemetryCsv from "helpers/hooks/csv/useTelemetryCsv"
import useCsvDownloadSources from "helpers/hooks/csv/useCsvDownloadSources"
import useDateRange from "helpers/hooks/useDateRange"
import LoadingButton from "widgets/common/LoadingButton"

import CsvDateRangeSection from "./CsvDateRangeSection"
import CsvSourceAccordion from "./CsvSourceAccordion"
import CsvFormatPreference from "./CsvFormatPreference"

const MAX_RANGE_MONTHS = 12

interface CsvDownloadDrawerProps {
  open: boolean
  onClose: () => void
  entity: EntityType
}

const CsvDownloadDrawer: FC<CsvDownloadDrawerProps> = ({ open, onClose, entity }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { id } = useParams()
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"))

  const { download, isFetching } = useTelemetryCsv()
  const { sources, isLoading } = useCsvDownloadSources(entity, Number(id))
  const { getDateRangeTools } = useDateRange()
  const { dateRange } = useMemo(
    () => getDateRangeTools(entity),
    [getDateRangeTools, entity],
  )

  const [csvRange, setCsvRange] = useState<CsvDateRange | null>(null)
  const [format, setFormat] = useState<CsvDownloadFormat>("zip")
  const [selectedByKey, setSelectedByKey] = useState<Record<string, Set<string>>>({})
  // Only one source stays open at a time, so this holds its key.
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  // Snapshot the active dashboard period only on the open transition, so the
  // drawer freezes the page's current range. A ref keeps `dateRange` out of
  // the effect deps so its updates don't re-trigger the snapshot.
  const dateRangeRef = useRef(dateRange)
  dateRangeRef.current = dateRange

  // Reset on open. The selection is (re)seeded by the effect below once the
  // sources are ready; `seededRef` makes that seed run exactly once per open.
  const seededRef = useRef(false)

  useEffect(() => {
    if (!open) {
      seededRef.current = false
      return
    }
    const current = dateRangeRef.current
    setSelectedByKey({})
    setFormat("zip")
    setExpandedKey(null)
    seededRef.current = false
    setCsvRange(
      current
        ? {
            range: current.range,
            fromDate: DateTime.fromISO(current.fromDate),
            toDate: DateTime.fromISO(current.toDate),
            zoom: current.zoom,
          }
        : null,
    )
  }, [open])

  // Seed every telemetry as selected once loading has settled. Gating on
  // `!isLoading` guarantees the def set is final and `sources` is stable, so a
  // plain "selected" set is safe (no stale-selection race).
  useEffect(() => {
    if (!open || isLoading || seededRef.current) return
    const map: Record<string, Set<string>> = {}
    sources.forEach((source) => {
      map[source.key] = new Set(source.telemetries.map((tel) => tel.apiName))
    })
    setSelectedByKey(map)
    seededRef.current = true
  }, [open, isLoading, sources])

  const toggleTelemetry = useCallback((sourceKey: string, apiName: string) => {
    setSelectedByKey((prev) => {
      const current = new Set(prev[sourceKey] ?? [])
      if (current.has(apiName)) current.delete(apiName)
      else current.add(apiName)
      return { ...prev, [sourceKey]: current }
    })
  }, [])

  const toggleAll = useCallback(
    (sourceKey: string, checked: boolean) => {
      const source = sources.find((s) => s.key === sourceKey)
      if (!source) return
      setSelectedByKey((prev) => ({
        ...prev,
        [sourceKey]: checked
          ? new Set(source.telemetries.map((tel) => tel.apiName))
          : new Set(),
      }))
    },
    [sources],
  )

  const selectedSources = useMemo(
    () => sources.filter((s) => (selectedByKey[s.key]?.size ?? 0) > 0),
    [sources, selectedByKey],
  )

  const fileCount = selectedSources.length
  const showFormatPreferences = fileCount > 1
  const effectiveFormat: CsvDownloadFormat = showFormatPreferences ? format : "separate"
  const showSummaryBar = showFormatPreferences && effectiveFormat === "separate"

  const isRangeValid = useMemo(() => {
    if (!csvRange) return false
    const { fromDate, toDate } = csvRange
    if (!fromDate.isValid || !toDate.isValid) return false
    if (toDate <= fromDate) return false
    return toDate <= fromDate.plus({ months: MAX_RANGE_MONTHS })
  }, [csvRange])

  const canDownload =
    isRangeValid && selectedSources.length > 0 && !isFetching && !isLoading

  const handleConfirm = useCallback(async () => {
    if (!csvRange || !canDownload) return
    const groups: CsvDownloadGroup[] = selectedSources.map((source) => {
      const fields = Array.from(selectedByKey[source.key] ?? [])
      if (source.isWeather) {
        return {
          entity: source.entity,
          ids: [],
          measurement: source.measurement,
          fields,
          geohash: source.geohash,
        }
      }
      return {
        entity: source.entity,
        ids: source.id != null ? [source.id] : [],
        measurement: source.measurement,
        fields,
      }
    })

    const downloaded = await download({
      id: Number(id),
      fromDate: csvRange.fromDate.toISO() as string,
      toDate: csvRange.toDate.toISO() as string,
      entity,
      groups,
      format: effectiveFormat,
    })
    // On failure the error snackbar is already up; keep the drawer open so the range
    // and the selected sources survive for a retry.
    if (downloaded) onClose()
  }, [
    csvRange,
    canDownload,
    selectedSources,
    selectedByKey,
    download,
    id,
    entity,
    effectiveFormat,
    onClose,
  ])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isSmall ? "100%" : 740,
          maxWidth: "100%",
        },
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: 2.5, px: 3.5 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("csv_drawer.TITLE")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider />
        <Stack spacing={2.5} sx={{ py: 2.5, px: 3.5, overflowY: "auto", flex: 1 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t("csv_drawer.SELECT_DATE_RANGE")}
            </Typography>
            <CsvDateRangeSection
              entity={entity}
              value={csvRange}
              maxRangeMonths={MAX_RANGE_MONTHS}
              onChange={setCsvRange}
            />
          </Stack>
          <Divider />
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t("csv_drawer.SELECT_SOURCES")}
            </Typography>
            {isLoading ? (
              <Stack spacing={1}>
                {fp
                  .times((index) => index)(3)
                  .map((i) => (
                    <Skeleton key={i} variant="rounded" height={48} />
                  ))}
              </Stack>
            ) : sources.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                {t("csv_drawer.NO_SOURCES")}
              </Typography>
            ) : (
              <Box>
                {sources.map((source) => (
                  <CsvSourceAccordion
                    key={source.key}
                    source={source}
                    selected={selectedByKey[source.key] ?? new Set()}
                    expanded={expandedKey === source.key}
                    onExpandedChange={(isExpanded) =>
                      setExpandedKey(isExpanded ? source.key : null)
                    }
                    onToggleTelemetry={toggleTelemetry}
                    onToggleAll={toggleAll}
                  />
                ))}
              </Box>
            )}
          </Stack>
          {showFormatPreferences && (
            <>
              <Divider />
              <CsvFormatPreference value={format} onChange={setFormat} />
            </>
          )}
          {showSummaryBar && (
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon fontSize="small" />}
              sx={{
                bgcolor: (theme) => theme.palette.info.dark,
                alignSelf: "flex-start",
                pr: 7,
              }}
            >
              {t("csv_drawer.FILE_COUNT", { count: fileCount })}
            </Alert>
          )}
        </Stack>
        <Divider />
        <Box sx={{ py: 2.5, px: 3.5, display: "flex", justifyContent: "center" }}>
          <LoadingButton
            type="button"
            variant="contained"
            styles={{
              px: { xs: 2, sm: 10 },
              width: { xs: "100%", sm: "auto" },
            }}
            onClick={handleConfirm}
            disabled={!canDownload}
            loading={isFetching}
          >
            {t("csv_drawer.CONFIRM_DOWNLOAD")}
          </LoadingButton>
        </Box>
      </Stack>
    </Drawer>
  )
}

export default CsvDownloadDrawer
